'use client';

import { useEffect, useRef, useState } from 'react';
import { Building2, Eye, Shield, SlidersHorizontal, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const topFeatures = [
  {
    icon: Building2,
    title: 'Universal Platform',
    description: 'Manage any property type from one dashboard — no switching tools, no workarounds.',
  },
  {
    icon: Eye,
    title: 'Real-Time Visibility',
    description: 'Live dashboards for you, your team, and your owners. No more guessing.',
  },
  {
    icon: Shield,
    title: 'Vendor Accountability',
    description: 'Track performance, ratings, certifications, and response times for every vendor.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Fully Configurable',
    description: 'Customize workflows, labels, and terminology per property type. Your platform, your rules.',
  },
];

const FeaturesHighlight = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="features"
      className="relative py-14 sm:py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Platform Features
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Enterprise Technology.
            <span className="text-primary-600 dark:text-primary-400"> Accessible Pricing.</span>
          </h2>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
          {topFeatures.map((feature, i) => (
            <div
              key={i}
              className={cn(
                "group relative p-4 sm:p-6 rounded-2xl",
                "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none",
                "border border-gray-200 dark:border-white/[0.06]",
                "hover:border-primary-500/20 hover:bg-gray-50 dark:hover:bg-white/[0.04]",
                "transition-all duration-500",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${200 + i * 75}ms` }}
            >
              <div className={cn(
                "w-11 h-11 rounded-xl mb-4 flex items-center justify-center",
                "bg-primary-500/10 border border-primary-500/20",
                "group-hover:bg-primary-500/20 group-hover:border-primary-500/30",
                "transition-all duration-300"
              )}>
                <feature.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-secondary-100 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-secondary-400 leading-relaxed">
                {feature.description}
              </p>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Link to full features */}
        <div
          className={cn(
            "text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '600ms' }}
        >
          <Link
            href="/features"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            See more
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export { FeaturesHighlight };
